import destination from "@turf/destination";
import { polygon, featureCollection } from "@turf/helpers";
import mapboxgl from "mapbox-gl";
import { HEXAGON_DATA, DENSITY_COLORS } from "./mapConstants";

// Función para crear un hexágono alrededor de un punto
export const createHexagon = (center, radiusMeters) => {
    const coords = [];
    for (let i = 0; i < 6; i++) {
        const vertex = destination(center, radiusMeters, i * 60, {
            units: "meters",
        });
        coords.push(vertex.geometry.coordinates);
    }
    coords.push(coords[0]);
    return polygon([coords], {});
};

// Función para generar la grilla de hexágonos en dos columnas
export const generateHexagonGridTwoColumns = (center, radiusMeters, count) => {
    const hexagons = [];

    const hexSide = radiusMeters;
    const hexWidth = 2 * hexSide;
    const hexHeight = Math.sqrt(3) * hexSide;
    const dx = 0.9 * hexWidth;
    const dy = 0.9 * hexHeight;
    const diagonalOffset = dx / 2;
    const offsetCenter = destination(center, dy / 2, 0, { units: "meters" });
    const offsetCenter1 = destination(offsetCenter, -diagonalOffset, 90, {
        units: "meters",
    });

    let id = 101;

    const basePt = destination(offsetCenter1, diagonalOffset, 270, {
        units: "meters",
    });

    for (let row = 1; id <= 100 + count; row++) {
        // Columna izquierda
        const offsetX1 = row * diagonalOffset;
        const offsetY1 = row * dy;

        const pt = destination(basePt, offsetX1, 90, { units: "meters" });
        const final = destination(pt, offsetY1, 0, { units: "meters" }).geometry
            .coordinates;

        const [dens, cant, peso, biom] = HEXAGON_DATA[id];
        const hex = createHexagon(final, radiusMeters);

        hex.properties = {
            // id,
            name: `Zona ${id}`,
            densidad: dens,
            cantidad: cant,
            pesoProm: peso,
            biom,
            centerCoords: JSON.stringify(final),
            fill: DENSITY_COLORS.getDensityColor(dens),
            info: `<strong>Zona ${id}</strong><br/>
             Densidad: ${dens} kg/m<sup>3</sup><br/>
             Cantidad: ${cant.toLocaleString()}<br/>
             Peso Promedio: ${peso} g<br/>
             Biomasa: ${biom} kg`,
        };
        hexagons.push(hex);
        if (++id > 100 + count) break;

        // Columna derecha
        const offsetX2 = offsetX1 + dx;
        const pt2 = destination(basePt, offsetX2, 90, { units: "meters" });
        const final2 = destination(pt2, offsetY1, 0, { units: "meters" }).geometry
            .coordinates;

        const [dens2, cant2, peso2, biom2] = HEXAGON_DATA[id];
        const hex2 = createHexagon(final2, radiusMeters);

        hex2.properties = {
            // id,
            name: `Zona ${id}`,
            densidad: dens2,
            cantidad: cant2,
            pesoProm: peso2,
            biom: biom2,
            centerCoords: JSON.stringify(final2),
            fill: DENSITY_COLORS.getDensityColor(dens2),
            info: `<strong>Zona ${id}</strong><br/>
             Densidad: ${dens2} kg/m<sup>3</sup><br/>
             Cantidad: ${cant2.toLocaleString()}<br/>
             Peso Promedio: ${peso2} g<br/>
             Biom: ${biom2} kg`,
        };
        hexagons.push(hex2);
        if (++id > 100 + count) break;
    }
    return hexagons;
};

// Función para generar hexágonos para todos los puntos
export const generateHexagonsForAllPoints = (points, hexagonConfig) => {
    const filteredPoints = points.filter((p) => p.showHexagons);
    const allHexagons = [];
    filteredPoints.forEach((point) => {
        const centerCoords = [point.longitude, point.latitude];
        const hexagons = generateHexagonGridTwoColumns(
            centerCoords,
            hexagonConfig.radiusMeters,
            hexagonConfig.count
        );
        allHexagons.push(...hexagons);
    });
    return allHexagons;
};

// Función para agregar hexágonos al mapa
export const addHexagonsToMap = (map, memoizedHexagons, addHexagonInteractivity) => {
    try {
        if (!memoizedHexagons.length) {
            return;
        }


        if (!map.getSource("hexagons")) {
            map.addSource("hexagons", {
                type: "geojson",
                data: featureCollection(memoizedHexagons),
            });

            map.addLayer({
                id: "hexagons-layer",
                type: "fill",
                source: "hexagons",
                paint: {
                    "fill-color": ["get", "fill"],
                    "fill-opacity": [
                        "case",
                        ["boolean", ["feature-state", "hover"], false],
                        0.9, // Más opaco en hover
                        0.8, // Opacidad alta para colores sólidos
                    ],
                },
            });

            // Capa de contorno de hexágonos
            map.addLayer({
                id: "hexagons-outline",
                type: "line",
                source: "hexagons",
                paint: {
                    "line-color": ["get", "fill"],
                    "line-width": 1,
                },
            });

            // Capa de etiquetas para hexágonos
            map.addLayer({
                id: "hexagons-labels",
                type: "symbol",
                source: "hexagons",
                layout: {
                    "text-field": ["get", "id"],
                    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                    "text-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        14,
                        0, // A zoom 14 y menor, tamaño 0 
                        15,
                        6, // A zoom 15, tamaño 6
                        18,
                        8, // A zoom 18, tamaño 8
                    ],
                    "text-anchor": "center",
                },
                paint: {
                    "text-color": "#ffffff",
                    "text-halo-color": "#000000",
                    "text-halo-width": 1.5,
                    "text-opacity": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        14,
                        0, // Invisible hasta zoom 14
                        15,
                        0.8, // Aparece gradualmente desde zoom 15
                        18,
                        1, // Completamente visible a zoom 18
                    ],
                },
            });

            addHexagonInteractivity(map);
        } else {
            map.getSource("hexagons").setData(featureCollection(memoizedHexagons));
        }
    } catch (error) {
    }
};

// Función para agregar interactividad a los hexágonos
export const addHexagonInteractivity = (map, handleMarkerClick) => {
    // Popup para mostrar información
    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: "rounded-4",
    });

    map.on("mouseenter", "hexagons-layer", (e) => {
        const feature = e.features[0];
        const coordinates = JSON.parse(feature.properties.centerCoords);
        const description = feature.properties.info;

        map.setFeatureState(
            { source: "hexagons", id: feature.id },
            { hover: true }
        );

        popup.setLngLat(coordinates).setHTML(description).addTo(map);
    });

    map.on("mouseleave", "hexagons-layer", () => {
        if (popup) popup.remove();
    });

    // Evento click 
    map.on("click", "hexagons-layer", (e) => {
        const feature = e.features[0];
        const hexagonInfo = {
            id: feature.properties.id,
            name: feature.properties.name,
            parentPoint: feature.properties.parentPoint,
            hexIndex: feature.properties.hexIndex,
            coordinates: JSON.parse(feature.properties.centerCoords),
        };

        if (handleMarkerClick) {
            handleMarkerClick({
                ...hexagonInfo,
                type: "hexagon",
            });
        }
        map.flyTo({
            center: hexagonInfo.coordinates,
            zoom: 17,
        });
    });
};
